import { forwardRef, useEffect, useImperativeHandle, useRef } from "react";
import {
  AmbientLight,
  Box3,
  BoxHelper,
  DirectionalLight,
  Mesh,
  MeshStandardMaterial,
  PerspectiveCamera,
  Scene,
  Vector3,
  WebGLRenderer,
} from "three";
import { OrbitControls } from "three-stdlib";
import { STLLoader } from "three-stdlib";

export interface StlPreviewHandle {
  /** Returns a Blob of the STL at the *current* live scale (for upload at quote time). */
  getScaledStlBlob: () => Promise<Blob>;

  /** Reset camera to a sensible default framing for the current model + scale. */
  resetView: () => void;
}

interface StlPreviewProps {
  file: File;
  scale?: number; // default 1.0; live updates via mesh.scale + ref objects
  onDimensionsChange?: (dims: { x: number; y: number; z: number; max: number }) => void;
}

/**
 * 3D STL preview with live uniform scaling and real-world dimension readouts (mm convention).
 * Users can freely rotate and zoom using mouse/touch controls.
 *
 * Scaling is applied in the browser; the scaled geometry is what gets uploaded for the
 * quote, so the slicer naturally reports the correct (larger) material/time.
 *
 * Non-destructive: original geometry is kept pristine for accurate export.
 */
export const StlPreview = forwardRef<StlPreviewHandle, StlPreviewProps>(
  ({ file, scale = 1, onDimensionsChange }, ref) => {
    const containerRef = useRef<HTMLDivElement | null>(null);

    // Three objects — never in React state
    const sceneRef = useRef<Scene | null>(null);
    const rendererRef = useRef<WebGLRenderer | null>(null);
    const cameraRef = useRef<PerspectiveCamera | null>(null);
    const meshRef = useRef<Mesh | null>(null);
    const boxHelperRef = useRef<BoxHelper | null>(null);
    const controlsRef = useRef<OrbitControls | null>(null);

    // Pristine original data for export + dimension math (mm convention)
    const originalGeometryRef = useRef<any>(null); // BufferGeometry from STLLoader
    const originalSizeRef = useRef<Vector3>(new Vector3());
    const originalCenterRef = useRef<Vector3>(new Vector3());
    const originalMaxDimRef = useRef<number>(1);

    let cancelledRef = useRef(false);
    let animationIdRef = useRef(0);

    // Small pure binary STL exporter (no extra deps). Produces a valid binary STL
    // with zeroed normals (slicers recompute anyway).
    function createBinaryStl(geometry: any, scaleFactor: number): ArrayBuffer {
      const positions = geometry.getAttribute("position");
      if (!positions) throw new Error("STL geometry has no position attribute");

      const count = positions.count; // vertices
      const triCount = Math.floor(count / 3);

      const buffer = new ArrayBuffer(84 + triCount * 50);
      const view = new DataView(buffer);
      let offset = 80; // skip header
      view.setUint32(offset, triCount, true);
      offset += 4;

      const p = positions.array as Float32Array;

      for (let i = 0; i < triCount; i++) {
        const i0 = i * 9;
        // normal (zeroed — acceptable for our use)
        view.setFloat32(offset, 0, true);
        view.setFloat32(offset + 4, 0, true);
        view.setFloat32(offset + 8, 0, true);
        offset += 12;

        // v0, v1, v2 (apply scale)
        for (let v = 0; v < 3; v++) {
          const base = i0 + v * 3;
          view.setFloat32(offset, p[base] * scaleFactor, true);
          view.setFloat32(offset + 4, p[base + 1] * scaleFactor, true);
          view.setFloat32(offset + 8, p[base + 2] * scaleFactor, true);
          offset += 12;
        }
        // attribute byte count
        view.setUint16(offset, 0, true);
        offset += 2;
      }
      return buffer;
    }

    useImperativeHandle(ref, () => ({
      async getScaledStlBlob(): Promise<Blob> {
        const geo = originalGeometryRef.current;
        if (!geo) throw new Error("No geometry loaded yet");
        const s = Math.max(0.01, scale || 1);
        const buf = createBinaryStl(geo, s);
        return new Blob([buf], { type: "model/stl" });
      },

      resetView() {
        const cam = cameraRef.current;
        const ctrl = controlsRef.current;
        const max = originalMaxDimRef.current;
        if (!cam || !ctrl || !max) return;

        const dist = max * 2.2;
        cam.position.set(dist, dist * 0.7, dist);
        cam.lookAt(0, 0, 0);
        ctrl.target.set(0, 0, 0);
        ctrl.update();
      },
    }));

    // Heavy setup — only when the File object changes
    useEffect(() => {
      const container = containerRef.current;
      if (!container) return;

      cancelledRef.current = false;

      const scene = new Scene();
      sceneRef.current = scene;
      scene.add(new AmbientLight(0xffffff, 0.6));
      const dir = new DirectionalLight(0xffffff, 0.8);
      dir.position.set(1, 1, 1);
      scene.add(dir);

      const renderer = new WebGLRenderer({ antialias: true, alpha: true });
      rendererRef.current = renderer;
      renderer.setPixelRatio(window.devicePixelRatio);
      renderer.setSize(container.clientWidth, container.clientHeight);
      container.appendChild(renderer.domElement);

      const camera = new PerspectiveCamera(
        45,
        container.clientWidth / container.clientHeight,
        0.1,
        8000,
      );
      cameraRef.current = camera;

      // Resize handler (improvement over original)
      const onResize = () => {
        if (!container || !renderer || !camera) return;
        camera.aspect = container.clientWidth / container.clientHeight;
        camera.updateProjectionMatrix();
        renderer.setSize(container.clientWidth, container.clientHeight);
      };
      window.addEventListener("resize", onResize);

      const material = new MeshStandardMaterial({ color: 0x4a90e2, flatShading: true });

      file
        .arrayBuffer()
        .then((buf) => {
          if (cancelledRef.current) return;

          const geometry = new STLLoader().parse(buf);
          geometry.computeBoundingBox();
          const bbox = geometry.boundingBox ?? new Box3();
          const size = new Vector3();
          bbox.getSize(size);
          const center = new Vector3();
          bbox.getCenter(center);

          // Store pristine copy for export + math
          originalGeometryRef.current = geometry; // keep original (no mutation)
          originalSizeRef.current.copy(size);
          originalCenterRef.current.copy(center);
          originalMaxDimRef.current = Math.max(size.x, size.y, size.z);

          // Non-destructive centering: translate a *clone* for the viz mesh only
          const vizGeo = geometry.clone();
          vizGeo.translate(-center.x, -center.y, -center.z);

          const mesh = new Mesh(vizGeo, material);
          meshRef.current = mesh;
          scene.add(mesh);

          // Visual bbox helper (orange dashed box around the model)
          const boxHelper = new BoxHelper(mesh, 0xffaa00);
          boxHelperRef.current = boxHelper;
          scene.add(boxHelper);

          // Initial camera framing (only on first load)
          const maxDim = originalMaxDimRef.current;
          const camDist = maxDim * 2.2;
          camera.position.set(camDist, camDist * 0.7, camDist);
          camera.lookAt(0, 0, 0);

          // OrbitControls - user can now freely rotate and zoom
          const controls = new OrbitControls(camera, renderer.domElement);
          controls.enableDamping = true;
          controls.dampingFactor = 0.12;
          controls.enablePan = false;           // keep it simple
          controls.minDistance = 20;
          controls.maxDistance = 4000;
          controls.target.set(0, 0, 0);
          controlsRef.current = controls;

          // Kick off RAF
          const animate = () => {
            if (cancelledRef.current || !rendererRef.current || !cameraRef.current) return;

            controls.update(); // required for damping + controls to work
            rendererRef.current.render(sceneRef.current!, cameraRef.current);
            animationIdRef.current = requestAnimationFrame(animate);
          };
          animate();

          // Prime the dimension callback with scale=1
          if (onDimensionsChange) {
            onDimensionsChange({
              x: size.x,
              y: size.y,
              z: size.z,
              max: originalMaxDimRef.current,
            });
          }
        })
        .catch(() => {
          /* swallow — parent already shows upload errors; preview is best-effort */
        });

      return () => {
        cancelledRef.current = true;
        window.removeEventListener("resize", onResize);
        cancelAnimationFrame(animationIdRef.current);

        // Dispose OrbitControls
        if (controlsRef.current) {
          controlsRef.current.dispose();
          controlsRef.current = null;
        }

        if (renderer) {
          renderer.dispose();
          if (renderer.domElement.parentElement === container) {
            container.removeChild(renderer.domElement);
          }
        }
        if (meshRef.current) {
          meshRef.current.geometry.dispose();
          (meshRef.current.material as MeshStandardMaterial).dispose();
        }
        // Three helpers / refs do not all require explicit dispose for this scope
      };
    }, [file]); // only re-parse on new file

    // Live scale + reference object positioning + dimension callback + camera re-framing
    useEffect(() => {
      const s = Math.max(0.01, scale || 1);
      const mesh = meshRef.current;
      const boxHelper = boxHelperRef.current;
      const origSize = originalSizeRef.current;
      const origCenter = originalCenterRef.current;
      const origMax = originalMaxDimRef.current;

      if (mesh) {
        mesh.scale.setScalar(s);
        // Keep the visual center stable while scaling (prevents drift)
        mesh.position.set(-origCenter.x * s, -origCenter.y * s, -origCenter.z * s);
      }
      if (boxHelper) boxHelper.update();

      // Note: We no longer forcefully move the camera on scale changes.
      // OrbitControls now owns the view — user can rotate/zoom freely.
      // The model scales around its center while the camera stays where the user left it.

      if (onDimensionsChange && origSize) {
        onDimensionsChange({
          x: origSize.x * s,
          y: origSize.y * s,
          z: origSize.z * s,
          max: origMax * s,
        });
      }
    }, [scale, onDimensionsChange]);

    return (
      <div
        ref={containerRef}
        data-testid="stl-preview"
        className="h-64 w-full rounded-lg border border-border bg-muted"
      />
    );
  },
);

StlPreview.displayName = "StlPreview";
